import t from 'react-translate';
import { useSelector } from 'react-redux';
import CollapsiblePanel from 'athena/components/collapsible-panel';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { useContext, useEffect, useState } from 'react';
import {
  getMentoringProgramGoalItems,
  getMentoringProgramGoals,
} from 'redux/selectors/mentorship-program-goal-items';
import { useMentoringProgramGoals } from 'athena/hooks/use-mentoring-program-goals';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import useConnection from 'athena/hooks/use-connection';
import { useInView } from 'react-intersection-observer';
import { DEFAULT_GOAL_ITEM } from 'athena/components/goal-item/constants';
import { animated, useTransition } from '@react-spring/web';
import { MentoringProgramGoalItem } from 'redux/schemas/models/mentoring-program-goal-items';
import GoalItem from 'athena/components/goal-item';
import moment from 'moment';
import { PanelStateKey } from 'athena/components/constants';
import { css } from '@emotion/react';
import {
  headerLineHeight,
  textLargeBodyFontSize,
} from 'styles/global_defaults/fonts';
import { gray2 } from 'styles/global_defaults/colors';
import GoalSettingGuide from './goal-setting-guide';
import { config } from '@config/pendo.config.json';

const styles = css`
  font-size: ${textLargeBodyFontSize}px;
  line-height: ${headerLineHeight}px;
  color: ${gray2};
`;

const GoalsList = () => {
  const { ref: goalItemsRef, inView: isGoalsEndReached } = useInView();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentorshipProgramId,
    mentorshipProgramEnrollment: currentEnrollmentId,
    matchAnnouncementDate,
    guidanceEnabled,
  } = mentoringProgram || {};
  const { connection } = useConnection(currentEnrollmentId);
  const { assignedEnrollment } = connection || {};
  const { id: connectionEnrollmentId, user } = assignedEnrollment || {};
  const { firstName: connectionFirstName } = user || {};
  const { isMentor, isMentee } = useMentoringProgramRole();
  const enrollmentId = isMentee ? currentEnrollmentId : connectionEnrollmentId;
  const [editingId, setEditingId] = useState<number>();

  const {
    loadMore,
  }: {
    loadMore: () => void;
  } = useMentoringProgramGoals({ mentorshipProgramId, enrollmentId });

  const { totalCount, cmeVideoUrl } = useSelector(getMentoringProgramGoalItems(enrollmentId)) || {};
  const goalItems = useSelector(getMentoringProgramGoals(enrollmentId)) || [];
  const getDefaultGoalItem = () => ({ ...DEFAULT_GOAL_ITEM });

  const animationConfig = {
    keys: (item) => item.id,
    from: { opacity: 0, height: 0 },
    enter: { opacity: 1, height: 'auto' },
    leave: { opacity: 0, height: 0 },
    config: { duration: 300 },
  };

  const goalTransitions = useTransition(
    [isMentor ? null : getDefaultGoalItem(), ...(goalItems || [])].filter(
      Boolean,
    ),
    animationConfig,
  );

  useEffect(() => {
    if (isGoalsEndReached) {
      loadMore();
    }
  }, [isGoalsEndReached, loadMore]);

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GOALS;
  const panelTitle = isMentee
    ? translationBase.MENTEE_TITLE(totalCount)
    : translationBase.MENTOR_TITLE(connectionFirstName, totalCount);

  const toGoalItem = (item: MentoringProgramGoalItem) => (
    <GoalItem
      isReadOnly={isMentor}
      item={item}
      isEditing={editingId === item.id}
      setEditingId={setEditingId}
    />
  );

  const announcementDateHasPassed = moment().isAfter(
    moment(matchAnnouncementDate),
  );

  const renderForCurrentRole = isMentee
    ? true
    : connection && announcementDateHasPassed;

  const emptyState = isMentor && !goalItems.length ? (
    <div css={styles} className='my-4 text-align-center semi-bold'>
      {translationBase.EMPTY_LIST(connectionFirstName)}
    </div>
  ) : null;

  return renderForCurrentRole ? (
    <CollapsiblePanel
      title={panelTitle}
      panelState={{
        persistState: true,
        localStorageKey: PanelStateKey.GOAL_ITEMS_STATE,
      }}
      ariaLabel={panelTitle}
      dataQa={config.pendo.athena.mentorshipProgram.participantHome.goals.list.goalsPanel}
    >
      <>
        <div className='mt-2 mb-4'>
          {goalTransitions((style, item) => (
            <animated.div style={style}>{toGoalItem(item)}</animated.div>
          ))}
          <div ref={goalItemsRef} />
        </div>
        {emptyState}
        {guidanceEnabled && <GoalSettingGuide videoUrl={cmeVideoUrl} />}
      </>
    </CollapsiblePanel>
  ) : null;
};

export default GoalsList;
